// src/Auth/AuthContext.js
import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import { logoutUser, getUserProfile, getOrganizerProfile } from "../Api/authApi";
import { refreshAccessToken } from "../Api/apiConfig";
import { toast } from "react-toastify";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) return JSON.parse(storedUser);

    const token = localStorage.getItem("access");
    return token ? jwtDecode(token) : null;
  });

  const [loading, setLoading] = useState(true);
  const [needsOrganizerProfile, setNeedsOrganizerProfile] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const intervalRef = useRef(null);

  // ✅ Check if user needs to complete organizer profile
  const checkOrganizerProfileNeeded = async (profile) => {
    if (profile?.role === 'organizer') {
      try {
        const organizerData = await getOrganizerProfile();
        const fieldsToCheck = ['organization_name', 'short_bio', 'phone', 'city', 'country', 'address', 'tax_id', 'payout_account'];
        let nullCount = 0;
        fieldsToCheck.forEach(field => {
          if (organizerData[field] === null || organizerData[field] === '') {
            nullCount++;
          }
        });
        return nullCount > 4;
      } catch (err) {
        console.error('Failed to fetch organizer profile:', err);
        return false;
      }
    }
    return false;
  };

  // ✅ Handle login success (manual or social)
  const handleAuthSuccess = async () => {
    try {
      let profile = await getUserProfile();
      if (profile.role === 'organizer') {
        const organizerData = await getOrganizerProfile();
        profile = { ...profile, organization_name: organizerData.organization_name, is_verified: organizerData.is_verified, is_published: organizerData.is_published };
      }
      setUser(profile);
      localStorage.setItem("user", JSON.stringify(profile));
      // Check if organizer profile needs completion
      const needsCompletion = await checkOrganizerProfileNeeded(profile);
      setNeedsOrganizerProfile(needsCompletion);

      return { profile, needsOrganizerProfile: needsCompletion };
    } catch (err) {
      console.error('Failed to fetch profile during auth success:', err);
      throw err;
    }
  };

  // ✅ Handle manual login
  const handleLogin = async () => {
    return await handleAuthSuccess();
  };

  // ✅ Handle organizer profile completion
  const handleOrganizerProfileComplete = () => {
    setNeedsOrganizerProfile(false);
  };

  // ✅ Handle logout
  const handleLogout = async (showToast = true) => {
    if (isLoggingOut) return; // Prevent multiple logouts
    // console.log("handleLogout called");
    setIsLoggingOut(true);
    try {
      await logoutUser();
    } catch (err) {
      console.error("Logout API failed", err);
    }
    localStorage.removeItem("user");
    setUser(null);
    if (showToast) {
      toast.success("Logged out successfully!");
    }
    // Reset logout flag after a brief delay to ensure state updates
    setTimeout(() => setIsLoggingOut(false), 100);
  };

  // ✅ Periodic token refresh
  useEffect(() => {
    if (intervalRef.current) return; // Prevent multiple intervals
    // console.log("Setting up periodic refresh");
    intervalRef.current = setInterval(async () => {
      // console.log("Periodic refresh triggered");
      try {
        await refreshAccessToken();
      } catch (err) {
        console.log("Auto refresh failed", err);
        handleLogout(false);
      }
    }, 15 * 60 * 1000); // every 15 minutes
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  // ✅ Load user profile if logged in
  useEffect(() => {
    // console.log("Init useEffect running");
    const init = async () => {
      const access = localStorage.getItem("access");
      if (access) {
        try {
          let data = await getUserProfile();
          if (data.role === 'organizer') {
            const organizerData = await getOrganizerProfile();
            data = { ...data, organization_name: organizerData.organization_name, is_verified: organizerData.is_verified, is_published: organizerData.is_published };
          }
          setUser(data);
          localStorage.setItem("user", JSON.stringify(data));

          // Check if organizer profile needs completion
          const needsCompletion = await checkOrganizerProfileNeeded(data);
          setNeedsOrganizerProfile(needsCompletion);
        } catch (err) {
          console.warn("Profile fetch failed, attempting token refresh...");
          try {
            const newAccess = await refreshAccessToken();
            const decoded = jwtDecode(newAccess);
            setUser(decoded);
          } catch {
            handleLogout(false);
          }
        }
      }
      setLoading(false);
    };
    init();
  }, []);
  return (
    <AuthContext.Provider
      value={{
        user,
        setUser, // expose setter to update after PATCH
        loading,
        needsOrganizerProfile,
        handleLogin,
        handleAuthSuccess,
        handleLogout,
        handleOrganizerProfileComplete,
        isAuthenticated: !!user,
        isLoggingOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );

};

export const useAuth = () => useContext(AuthContext);
